import * as React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton, { iconButtonClasses } from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import Menu from "@mui/joy/Menu";
import MenuButton from "@mui/joy/MenuButton";
import MenuItem from "@mui/joy/MenuItem";
import Dropdown from "@mui/joy/Dropdown";

import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";

import { listCategories } from "../api/methods";
import { Category } from "../api/interfaces";
import AlertDialogModal from "./ConfirmationModal";
import { RowMenuProps } from "../api/interfaces";

export default function CategoryTable() {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [typeFilter, setTypeFilter] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rows, setRows] = React.useState<Category[]>([]);
  const [selectedItemId, setSelectedItemId] = React.useState<string | null>(
    null
  );

  const handleOpen = (itemId: string) => {
    setSelectedItemId(itemId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const RowMenu: React.FC<RowMenuProps> = ({ id }) => {
    return (
      <Dropdown>
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{
            root: { variant: "plain", color: "neutral", size: "sm" },
          }}
        >
          <MoreHorizRoundedIcon />
        </MenuButton>
        <Menu size="sm" sx={{ minWidth: 140 }}>
          <MenuItem color="danger" onClick={() => handleOpen(id)}>
            Supprimer
          </MenuItem>
        </Menu>
      </Dropdown>
    );
  };

  const handleDeleteSuccess = () => {
    if (selectedItemId) {
      setRows((prevRows) =>
        prevRows.filter((row) => row._id !== selectedItemId)
      );
      setOpen(false);
    }
  };

  React.useEffect(() => {
    let isMounted = true;
    const fetchCategories = async () => {
      try {
        const response = await listCategories();
        setRows(response.data);
        console.log(response.data);
      } catch (error) {
        console.log("Failed to fetch categories");
      } finally {
        console.log("OK");
      }
    };

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  const rowsPerPage = 10;
  const uniqueTypes = Array.from(new Set(rows.map((row) => row?.type)));
  const indexOfLastRow = (currentPage + 1) * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  const handleTypeFilterChange = (
    event: React.SyntheticEvent | null,
    newValue: string | null
  ) => {
    console.log(newValue);
    setTypeFilter(newValue !== null ? newValue : "");
  };

  const filteredRows = rows.filter((row) => {
    return (
      row.type.includes(typeFilter) &&
      (row.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.diagnostic_category
          .toLowerCase()
          .includes(searchQuery.toLowerCase()))
    );
  });

  const currentRows = filteredRows.slice(indexOfFirstRow, indexOfLastRow);
  type PageItem = number | "...";

  const getPageNumbers = (): PageItem[] => {
    const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
    const pages: PageItem[] = [];
    const visiblePages = 2;

    // Generate pages around the current page
    for (
      let i = Math.max(1, currentPage - visiblePages);
      i <= Math.min(totalPages, currentPage + 1 + visiblePages);
      i++
    ) {
      pages.push(i);
    }
    if (typeof pages[0] === "number" && pages[0] > 1) {
      pages.unshift(1);
      if (typeof pages[1] === "number" && pages[1] !== 2) {
        pages.splice(1, 0, "...");
      }
    }
    if (
      typeof pages[pages.length - 1] === "number" &&
      pages[pages.length - 1] < totalPages
    ) {
      if (
        typeof pages[pages.length - 1] === "number" &&
        pages[pages.length - 1] !== totalPages - 1
      ) {
        pages.push("...");
      }
      pages.push(totalPages);
    }
    return pages;
  };

  const handleNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) setCurrentPage((prevPage) => prevPage - 1);
  };

  const renderFilters = () => (
    <React.Fragment>
      <FormControl size="sm">
        <FormLabel>Type</FormLabel>
        <Select
          size="sm"
          placeholder="Filter by type"
          value={typeFilter}
          onChange={handleTypeFilterChange}
          slotProps={{ button: { sx: { whiteSpace: "nowrap" } } }}
        >
          <Option value="">All</Option>
          {uniqueTypes.map((type) => (
            <Option key={type} value={type}>
              {type}
            </Option>
          ))}
        </Select>
      </FormControl>
    </React.Fragment>
  );
  return (
    <React.Fragment>
      <Box
        className="SearchAndFilters-tabletUp"
        sx={{
          borderRadius: "sm",
          py: 2,
          display: { xs: "none", sm: "flex" },
          flexWrap: "wrap",
          gap: 1.5,
          "& > *": {
            minWidth: { xs: "120px", md: "160px" },
          },
        }}
      >
        <FormControl sx={{ flex: 1 }} size="sm">
          <FormLabel>Search for order</FormLabel>
          <Input
            size="sm"
            placeholder="Search"
            startDecorator={<SearchIcon />}
            onChange={(event: any) => {
              setSearchQuery(event.target.value);
            }}
          />
        </FormControl>
        {renderFilters()}
      </Box>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          stickyHeader
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "12px",
          }}
        >
          <thead>
            <tr>
              <th style={{ width: 60, padding: "12px 12px" }}>Type</th>
              <th style={{ width: 80, padding: "12px 6px" }}>Code</th>
              <th style={{ width: 180, padding: "12px 6px" }}>
                Description du diagnostic
              </th>
              <th style={{ width: 80, padding: "12px 6px" }}>Entité</th>
              <th style={{ width: 40, padding: "12px 6px" }}></th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((row) => (
              <tr key={row._id}>
                <td>
                  <Typography level="body-xs">{row.type}</Typography>
                </td>
                <td>
                  <Typography level="body-xs">{row.code}</Typography>
                </td>
                <td>
                  <Typography level="body-xs">
                    {row.diagnostic_category}
                  </Typography>
                </td>
                <td>
                  <Typography level="body-xs">{row.entity}</Typography>
                </td>
                <td>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <RowMenu id={row._id} />
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
          {selectedItemId && (
            <AlertDialogModal
              item="category"
              id={selectedItemId}
              open={open}
              onClose={handleClose}
              onDeleteSuccess={handleDeleteSuccess}
            />
          )}
        </Table>
      </Sheet>
      <Box
        className="Pagination-laptopUp"
        sx={{
          pt: 2,
          gap: 1,
          [`& .${iconButtonClasses.root}`]: { borderRadius: "50%" },
          display: {
            xs: "none",
            md: "flex",
          },
        }}
      >
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          startDecorator={<KeyboardArrowLeftIcon />}
          onClick={handlePreviousPage}
          disabled={currentPage === 0}
        >
          Previous
        </Button>

        <Box sx={{ flex: 1 }} />
        {getPageNumbers().map((page) => (
          <IconButton
            key={typeof page === "number" ? page : "ellipsis"}
            size="sm"
            variant={currentPage + 1 === page ? "outlined" : "plain"}
            color="neutral"
            onClick={() => {
              if (typeof page === "number") setCurrentPage(page - 1);
            }}
            disabled={typeof page !== "number"}
          >
            {typeof page === "number" ? page : "..."}
          </IconButton>
        ))}
        <Box sx={{ flex: 1 }} />

        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          endDecorator={<KeyboardArrowRightIcon />}
          onClick={handleNextPage}
          disabled={indexOfLastRow >= filteredRows.length}
        >
          Next
        </Button>
      </Box>
    </React.Fragment>
  );
}
